import { ArrowRight, ArrowUpRight } from "lucide-react";

const KPIS = [
  { label: "Spend exposed to rising commodities", value: "$19.7M", sub: "base oil, steel, rubber, copper", color: "#ec1c8c", accent: true },
  { label: "Highest exposure", value: "Base oil", sub: "$9.3M · +11% YoY", color: "#f59e0b", accent: false },
  { label: "Active disruption signals", value: "3", sub: "India, Germany, Japan", color: "#7c3aed", accent: false },
  { label: "Internationally sourced", value: "59%", sub: "cost + risk dependency", color: "#d1d5db", accent: false },
];

const INDEX_SERIES = [
  { label: "Base oil", change: "+16%", color: "#ec1c8c", changeColor: "#ec1c8c", values: [100, 104, 107.5, 110, 113.5, 116] },
  { label: "Steel", change: "+8%", color: "#1e1b4b", changeColor: "#1e1b4b", values: [100, 102, 104, 105, 107, 108] },
  { label: "Rubber", change: "+12%", color: "#7c3aed", changeColor: "#7c3aed", values: [100, 103, 107, 108.5, 110.5, 112] },
  { label: "Copper", change: "+18%", color: "#10b981", changeColor: "#10b981", values: [100, 106, 111, 114, 116.5, 118] },
];

const EXPOSURE = [
  { commodity: "Base oil", spend: "$9.3M", trend: "Up", vol: "High" },
  { commodity: "Steel", spend: "$6M", trend: "Up", vol: "Med" },
  { commodity: "Rubber", spend: "$2.7M", trend: "Up", vol: "High" },
  { commodity: "Aluminium", spend: "$2M", trend: "Flat", vol: "Med" },
  { commodity: "Copper", spend: "$1.6M", trend: "Up", vol: "High" },
];

const SIGNALS = [
  { region: "India", signal: "Monsoon flooding + port congestion", exposure: "41% of spend", severity: "High" },
  { region: "Germany", signal: "Energy cost volatility", exposure: "19% of spend", severity: "Med" },
  { region: "Japan", signal: "Seismic + FX swing", exposure: "9% of spend", severity: "Low" },
];

const CATEGORIES = [
  { name: "Lubricants & engine oil", spend: "$9.3M", share: "19.4%", index: "Base oil", vol: "High vol", outlook: "Bearish for buyers" },
  { name: "Braking systems", spend: "$4.2M", share: "8.7%", index: "Steel HRC", vol: "Medium vol", outlook: "Neutral" },
  { name: "Bearings & power transmission", spend: "$3M", share: "6.2%", index: "Copper + steel", vol: "High vol", outlook: "Bearish for buyers" },
];

const Y_LABELS = [125, 111, 103, 95];
const Y_MIN = 95;
const Y_MAX = 125;

/** Catmull-Rom spline through the points, as a cubic-bezier SVG path. */
function smoothPath(pts: [number, number][]) {
  let d = `M${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x} ${c1y} ${c2x} ${c2y} ${p2[0]} ${p2[1]}`;
  }
  return d;
}

function CommodityIndexChart() {
  const w = 480;
  const h = 190;
  const padL = 30;
  const padT = 8;
  const padB = 22;
  const x = (i: number) => padL + (i * (w - padL)) / 5;
  const y = (v: number) => padT + ((Y_MAX - v) / (Y_MAX - Y_MIN)) * (h - padT - padB);

  return (
    <svg className="sm-chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Commodity price index, last 6 periods">
      {Y_LABELS.map((t) => (
        <g key={t}>
          <line x1={padL} x2={w} y1={y(t)} y2={y(t)} stroke="#e5e7eb" strokeDasharray="4 4" />
          <text x={0} y={y(t) + 4} fontSize="10" fill="#9ca3af">{t}</text>
        </g>
      ))}
      {INDEX_SERIES.map(({ label, color, values }) => (
        <path key={label} d={smoothPath(values.map((v, i) => [x(i), y(v)]))} fill="none" stroke={color} strokeWidth="2" />
      ))}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <text key={i} x={x(i)} y={h - 4} textAnchor="middle" fontSize="10" fill="#9ca3af">{`P${i + 1}`}</text>
      ))}
    </svg>
  );
}

const badgeClass = (level: string) => {
  const l = level.toLowerCase();
  if (l.startsWith("high")) return "sm-risk-badge sm-risk-badge--high";
  if (l.startsWith("med")) return "sm-risk-badge sm-risk-badge--medium";
  return "sm-risk-badge sm-risk-badge--low";
};

export function MarketInsightsPanel() {
  return (
    <div className="sm-panel">
      <div className="sm-kpi-grid">
        {KPIS.map(({ label, value, sub, color, accent }) => (
          <div key={label} className="sm-kpi" style={{ borderColor: color }}>
            <p className="sm-kpi__label">{label}</p>
            <p className={`sm-kpi__value${accent ? " sm-kpi__value--accent" : ""}`}>{value}</p>
            <p className="sm-kpi__sub">{sub}</p>
          </div>
        ))}
      </div>

      <div className="si-grid-2">
        <div className="si-card sm-card">
          <p className="sm-card__title sm-card__title--tight">Commodity Price Index</p>
          <p className="sm-card__subtitle sm-card__subtitle--muted">Web-sourced, indexed to 100, last 6 periods</p>
          <CommodityIndexChart />
          <div className="sm-index-legend">
            {INDEX_SERIES.map(({ label, color, change, changeColor }) => (
              <div key={label} className="sm-index-legend__row">
                <span className="sm-index-legend__dot" style={{ background: color }} />
                <span className="sm-index-legend__label">{label}</span>
                <strong style={{ color: changeColor }}>{change}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="si-card sm-card sm-card--flush">
          <div className="sm-card__head">
            <p className="sm-card__title">Commodity Exposure</p>
            <span className="sm-tag">Spend x Trend</span>
          </div>
          <table className="sm-table">
            <thead>
              <tr>
                <th>Commodity</th>
                <th className="sm-table__right">Exposed Spend</th>
                <th>Trend</th>
                <th>Volatility</th>
              </tr>
            </thead>
            <tbody>
              {EXPOSURE.map(({ commodity, spend, trend, vol }) => (
                <tr key={commodity}>
                  <td className="sm-table__strong">{commodity}</td>
                  <td className="sm-table__right sm-table__muted">{spend}</td>
                  <td className={trend === "Up" ? "sm-trend sm-trend--up" : "sm-table__muted"}>
                    {trend === "Up" ? (
                      <>
                        <ArrowUpRight size={12} aria-hidden="true" /> Up
                      </>
                    ) : (
                      "— Flat"
                    )}
                  </td>
                  <td><span className={`${badgeClass(vol)} sm-priority`}>{vol}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="si-card sm-card sm-card--flush">
        <div className="sm-card__head sm-card__head--stacked">
          <div>
            <p className="sm-card__title">Supply Chain Disruption Signals</p>
            <p className="sm-card__subtitle sm-card__subtitle--muted">Geographic exposure mapped to active web signals</p>
          </div>
          <span className="sm-tag">MICI</span>
        </div>
        <table className="sm-table">
          <thead>
            <tr>
              <th>Region</th>
              <th>Signal</th>
              <th className="sm-table__right">Exposure</th>
              <th>Severity</th>
            </tr>
          </thead>
          <tbody>
            {SIGNALS.map(({ region, signal, exposure, severity }) => (
              <tr key={region}>
                <td className="sm-table__strong">{region}</td>
                <td className="op-purple">{signal}</td>
                <td className="sm-table__right sm-table__muted">{exposure}</td>
                <td><span className={`${badgeClass(severity)} sm-priority`}>{severity}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sm-callout">
          <div className="sm-callout__body">
            <p className="sm-callout__eyebrow">From: Market Insights</p>
            <p className="sm-callout__text">
              <strong>$19.7M of spend sits in commodities trending up, and 41% of spend is in monsoon-exposed India.</strong>{" "}
              Index price clauses on lubricants, and pre-position buffer stock or dual-source the India base before peak season.
            </p>
          </div>
          <button type="button" className="sm-callout__btn">
            Send to Opportunity
            <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <div className="si-card sm-card">
        <div className="sm-card__head sm-card__head--stacked sm-card__head--flat">
          <div>
            <p className="sm-card__title">Category Watch</p>
            <p className="sm-card__subtitle sm-card__subtitle--muted">
              Cost structure, market outlook, and <span className="op-purple">negotiation</span> levers per priority category
            </p>
          </div>
          <span className="sm-tag">Click a category</span>
        </div>
        <div className="sm-watch-grid">
          {CATEGORIES.map(({ name, spend, share, index, vol, outlook }) => (
            <div key={name} className="sm-watch">
              <p className="sm-watch__name">{name}</p>
              <p className="sm-watch__spend">
                {spend} <span>{share}</span>
              </p>
              <div className="sm-watch__meta">
                <span>Index: {index}</span>
                <span className={`${badgeClass(vol)} sm-priority`}>{vol}</span>
              </div>
              <p className={`sm-watch__outlook${outlook === "Neutral" ? " sm-watch__outlook--neutral" : ""}`}>{outlook}</p>
              <button type="button" className="sm-watch__link">View cost structure →</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
